/**
 * incomingCall — the pure state machine behind the incoming-call ring.
 *
 * ── Why this module exists ───────────────────────────────────────────────────
 * BHABY's consultation rooms are presence-based: a participant joins a
 * session-scoped Socket.IO room and negotiation starts when the other side
 * arrives. Nothing ever told an idle participant that a call had STARTED — the
 * only way to reach a consultation was to navigate to it by hand. The server now
 * rings the absent counterpart (`consultation:incoming`); this module owns the
 * client-side truth about what that means.
 *
 * It is deliberately pure — no React, no timers, no audio — so every rule the
 * ring depends on is unit-testable without a browser:
 *
 *   - a MALFORMED or self-addressed invite is refused, never rendered;
 *   - a DUPLICATE invite cannot ring twice (`handled` keys);
 *   - a LATE duplicate after accept/decline/timeout cannot restart the ring;
 *   - a genuinely NEW call for the SAME booking (a new session) still rings;
 *   - the ring stops on accept, decline, timeout and authoritative end;
 *   - the recipient is only ever routed to THEIR OWN role's room.
 */

/** Who is calling, as far as the UI is concerned. */
export interface IncomingCall {
  bookingId: number;
  sessionId: number;
  fromUid: string | null;
  fromName: string;
  fromRole: string;
  startedAt: string | null;
}

/**
 * `ringing` is the only state that may make a sound.
 * `accepted` / `declined` / `missed` are terminal for THIS invite and exist so
 * the UI can tell the user what happened without inventing an outcome.
 */
export type IncomingCallState = 'idle' | 'ringing' | 'accepted' | 'declined' | 'missed';

export interface IncomingCallMachine {
  state: IncomingCallState;
  call: IncomingCall | null;
  /**
   * Invite keys already acted on. This is what makes the ring idempotent: a
   * re-delivered event (socket reconnect, two tabs, a duplicate emit) can never
   * start a second ring.
   */
  handled: string[];
}

export const initialIncomingCall: IncomingCallMachine = { state: 'idle', call: null, handled: [] };

/** How many handled keys to remember — enough to cover a long shift, bounded. */
export const HANDLED_HISTORY_LIMIT = 50;

export type IncomingCallEvent =
  | { type: 'incoming'; call: IncomingCall }
  | { type: 'accept' }
  | { type: 'decline' }
  | { type: 'timeout' }
  /** Authoritative "that video session ended" (either participant hung up). */
  | { type: 'ended'; bookingId?: number | null }
  | { type: 'reset' };

const positiveInt = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const optionalString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/**
 * Validate a raw `consultation:incoming` payload.
 *
 * Anything that does not identify a real booking + OPEN session is refused, so a
 * malformed or hostile frame can never produce an incoming-call screen. An
 * invite addressed to the recipient BY the recipient (`fromUid === selfUid`) is
 * also refused: a socket cannot call itself, and rendering it would be a lie.
 */
export function parseIncomingCall(payload: unknown, selfUid?: string | null): IncomingCall | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;

  const bookingId = positiveInt(raw.bookingId);
  const sessionId = positiveInt(raw.sessionId);
  if (bookingId === null || sessionId === null) return null;

  const fromUid = optionalString(raw.fromUid);
  if (fromUid && selfUid && fromUid === selfUid) return null;

  return {
    bookingId,
    sessionId,
    fromUid,
    fromName: optionalString(raw.fromName) || '',
    fromRole: optionalString(raw.fromRole) || '',
    startedAt: optionalString(raw.startedAt),
  };
}

/** Identity of one invite: a NEW video session for a booking is a NEW call. */
export function incomingCallKey(call: IncomingCall): string {
  return `${call.bookingId}:${call.sessionId}`;
}

/**
 * The room the recipient must open to answer. Both roles are routed only into
 * their OWN surface — a patient is never sent to a doctor route.
 */
export function roomPathFor(call: IncomingCall, role: string | null | undefined): string | null {
  if (!call) return null;
  if (role === 'patient') return `/patient/video/${call.bookingId}`;
  if (role === 'doctor') return `/doctor/video/${call.bookingId}`;
  return null;
}

/**
 * Apply one event to the ring machine.
 *
 * The duplicate rule is the important one: once an invite key has been handled,
 * every later event carrying that SAME key is ignored entirely — so a reconnect
 * or a repeated emit cannot resurrect a call the user already accepted, declined
 * or missed.
 */
export function reduceIncomingCall(
  machine: IncomingCallMachine,
  event: IncomingCallEvent
): IncomingCallMachine {
  switch (event.type) {
    case 'incoming': {
      const key = incomingCallKey(event.call);
      if (machine.handled.includes(key)) return machine;
      const handled = [...machine.handled, key].slice(-HANDLED_HISTORY_LIMIT);
      return { state: 'ringing', call: event.call, handled };
    }
    case 'accept':
      if (machine.state !== 'ringing') return machine;
      return { ...machine, state: 'accepted' };
    case 'decline':
      if (machine.state !== 'ringing') return machine;
      return { ...machine, state: 'declined', call: null };
    case 'timeout':
      if (machine.state !== 'ringing') return machine;
      return { ...machine, state: 'missed', call: null };
    case 'ended': {
      if (machine.state !== 'ringing' || !machine.call) return machine;
      // A different booking ending must not stop this ring.
      const endedBooking = event.bookingId == null ? null : Number(event.bookingId);
      if (endedBooking != null && endedBooking !== machine.call.bookingId) return machine;
      return { ...machine, state: 'idle', call: null };
    }
    case 'reset':
      return { ...initialIncomingCall, handled: machine.handled };
    default:
      return machine;
  }
}
